import React, { BaseSyntheticEvent } from 'react'
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonViewDidLeave,
} from '@ionic/react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { chevronBackOutline } from 'ionicons/icons'
import { useHistory } from 'react-router'
import './AddMedicationAdministered.page.css'
import { getAddMedicationAdministeredSelector } from '../selectors'
import AddMedicationAdministeredContent from '../pageContent/AddMedicationAdministered.content'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import PCRHeader from '../../../components/pcrHeader/PCRHeader.component'
import { saveMedicationAdministered } from '../store/addMedicationAdministered.actions'
import { showAlert } from '../../../components/alert/store/alert.actions'
import { initialAddMedicationAdministeredState } from '../store/addMedicationAdministered.reducers'
import { compareObjectsIfEqual } from '../../../utilities/object.util'
import { setDeleteProcedure, setProcedureLoaderItem } from '../../timeline/store/timeline.actions'
import { setIsFirstVisit } from '../../../components/procedure/store/procedure.actions'
import { goBack } from '../../../utilities/navigation.util'
import { IServiceConfig } from '../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../configurations/selectors/serviceConfig.selector'
import { getCurrentDate, getCurrentTime } from '../../../utilities'
import { IAlertState } from '../../../components/alert/store/alert.types'
import { TimelineConstants } from '../../timeline/store/timeline.constants'

const AddMedicationAdministered: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch()
  const addMedicationAdministeredState = useSelector(getAddMedicationAdministeredSelector)
  const { pta, addMedicationAdministeredData, date, time, authorization, comments, assessedBy } =
    addMedicationAdministeredState
  const serviceConfigData: IServiceConfig = useSelector(getServiceConfigSelector)
  const { DISPLAY_SECONDS } = serviceConfigData
  const displaySeconds = !!DISPLAY_SECONDS

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if ((!date || !time) && !pta) {
      const alert: IAlertState = {
        message: t('common.saveTimelineEntry'),
        buttons: [
          {
            text: t('common.buttons.Yes'),
            handler: () => {
              const date = getCurrentDate()
              let time = getCurrentTime()
              if (!displaySeconds) {
                time = time.replace(/(:\d+)(:\d+)$/, '$1:00')
              }

              dispatch({ type: 'addMedicationAdministered/SET_TIME', time: time })
              dispatch({ type: 'addMedicationAdministered/SET_DATE', date: date })

              dispatch(saveMedicationAdministered())

              goBack()
            },
          },
          {
            text: t('common.buttons.No'),
            role: 'destructive',
            handler: () => {
              const itemToRemove = {
                key: addMedicationAdministeredState.key,
                rowActionType: TimelineConstants.SET_ADD_MEDICATION_ADMINISTERED_ITEM,
              }
              dispatch(setDeleteProcedure(true))
              dispatch({ type: TimelineConstants.DELETE_TIMELINE_ITEM, payload: itemToRemove })
              goBack()
            },
          },
        ],
      }
      dispatch(showAlert(alert))
      dispatch(setProcedureLoaderItem(false))
    } else {
      const dateTimeValidationCheck = initialStateCheck()
      if (dateTimeValidationCheck) {
        goBack()
      }
      dispatch(setProcedureLoaderItem(false))
    }
  }

  const initialStateCheck = () => {
    if (pta && ((date && time) || (!date && !time))) return true
    const currentState = { addMedicationAdministeredData, date, time, authorization, comments, assessedBy }
    const initalState = {
      addMedicationAdministeredData: initialAddMedicationAdministeredState.addMedicationAdministeredData,
      date: initialAddMedicationAdministeredState.date,
      time: initialAddMedicationAdministeredState.time,
      authorization: initialAddMedicationAdministeredState.authorization,
      comments: initialAddMedicationAdministeredState.comments,
      assessedBy: initialAddMedicationAdministeredState.assessedBy,
    }
    if (!compareObjectsIfEqual(currentState, initalState)) {
      return dateTimeValidationCheck()
    }
    return true
  }

  const dateTimeValidationCheck = () => {
    if (!date || !time) {
      dispatch(showAlert({ header: '', message: 'Date / Time is required' }))
      return false
    }
    return true
  }

  const saveOnClick = () => {
    if (!pta && (!date || !time)) {
      dateTimeValidationCheck()
      return
    }
    const dateTimeValidationCheckResult = initialStateCheck()
    if (dateTimeValidationCheckResult) {
      history.push(TabConstants.TIMELINE_TAB)
    }
  }

  useIonViewDidLeave(() => {
    dispatch(setIsFirstVisit(false))
  })

  return (
    <IonPage id='AddMedicationAdministered'>
      <PCRHeader />
      <IonHeader>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' onClick={onBackClick} color='primary-alt'>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonButtons slot='end'>
            <IonButton data-testid='Save' fill='clear' onClick={saveOnClick} color='primary-alt'>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
          <IonTitle data-testid='HeaderTitle'>{t('addMedicationAdministered.medication')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent id='add-medications-administered-content' color='light' fullscreen>
        <AddMedicationAdministeredContent isPHI />
      </IonContent>
    </IonPage>
  )
}

export default AddMedicationAdministered
